import { useCallback, useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import ExportMenu from '../../Shared/ExportMenu'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import useDebounce from '../../../utils/useDebounce'
import { formatDateTime } from '../../../utils/format'
import {
  createPrinterZpl,
  deletePrinterZpl,
  editPrinterZpl,
  getAllPrinterZpl,
  previewZpl,
} from '../../../services/printerZplServices'
import { mensagemDeErro } from '../../../services/apiClient'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'

const EMPTY_FORM = {
  subCompanyId: '',
  name: '',
  zpl: '',
  active: true,
}

const EXPORT_COLUMNS = [
  { header: 'Nome', accessor: 'name' },
  { header: 'ZPL', accessor: 'zpl' },
  { header: 'Situação', accessor: row => (row.active ? 'Ativo' : 'Inativo') },
  { header: 'Criação', accessor: row => formatDateTime(row.createdAt) },
]

const PrinterZpl = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()
  const podeGerenciar = usePode(PERMISSOES.RFID_GERENCIAR)

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const [previewUrl, setPreviewUrl] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)

  const [itemToDelete, setItemToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useDebounce(
    () => {
      setAppliedSearch(search)
      setPage(0)
    },
    500,
    [search]
  )

  const fetchData = useCallback(async () => {
    if (!subCompanyId) {
      setRows([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const response = await getAllPrinterZpl({
        subCompanyId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        search: appliedSearch,
      })
      setRows(response.data.result || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      setRows([])
      setTotal(0)
      notify(mensagemDeErro(error, 'Não foi possível carregar as etiquetas ZPL.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [subCompanyId, page, rowsPerPage, appliedSearch, notify])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Export leva todo o filtro, não só a página: pagina a API em lotes.
  const fetchAllForExport = useCallback(async () => {
    if (!subCompanyId) return []
    const BLOCO = 200
    const todas = []
    let offset = 0
    for (;;) {
      const response = await getAllPrinterZpl({
        subCompanyId,
        limit: BLOCO,
        offset,
        search: appliedSearch,
      })
      const itens = response.data.result || []
      todas.push(...itens)
      offset += BLOCO
      if (offset >= (response.data.total || 0) || itens.length === 0) break
    }
    return todas
  }, [subCompanyId, appliedSearch])

  const limparPreview = () => {
    setPreviewUrl(current => {
      if (current) URL.revokeObjectURL(current)
      return ''
    })
  }

  const openCreate = () => {
    setEditingId('')
    setForm({ ...EMPTY_FORM, subCompanyId })
    limparPreview()
    setDialogOpen(true)
  }

  const openEdit = row => {
    setEditingId(row.id)
    setForm({
      subCompanyId: row.subCompanyId || subCompanyId || '',
      name: row.name || '',
      zpl: row.zpl || '',
      active: row.active !== false,
    })
    limparPreview()
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditingId('')
    setForm(EMPTY_FORM)
    limparPreview()
  }

  const updateForm = field => event =>
    setForm(current => ({ ...current, [field]: event.target.value }))

  const isFormValid =
    Boolean(form.subCompanyId) && Boolean(form.name.trim()) && Boolean(form.zpl.trim())

  const handlePreview = async () => {
    if (!form.zpl.trim()) {
      notify('Informe o ZPL para pré-visualizar.', 'warning')
      return
    }
    setPreviewLoading(true)
    try {
      const url = await previewZpl(form.zpl)
      limparPreview()
      setPreviewUrl(url)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível gerar a pré-visualização.'), 'error')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSave = async () => {
    if (!isFormValid) {
      notify('Preencha todos os campos obrigatórios.', 'warning')
      return
    }
    setSaving(true)
    try {
      const payload = {
        subCompanyId: form.subCompanyId,
        name: form.name.trim(),
        zpl: form.zpl.trim(),
        active: form.active,
      }
      if (editingId) {
        await editPrinterZpl({
          zplTemplateId: editingId,
          name: payload.name,
          zpl: payload.zpl,
          active: payload.active,
        })
        notify('Etiqueta atualizada com sucesso!', 'success')
      } else {
        await createPrinterZpl(payload)
        notify('Etiqueta criada com sucesso!', 'success')
      }
      closeDialog()
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar a etiqueta ZPL.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!itemToDelete) return
    setDeleting(true)
    try {
      await deletePrinterZpl(itemToDelete.id)
      notify('Etiqueta excluída com sucesso!', 'success')
      setItemToDelete(null)
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível excluir a etiqueta ZPL.'), 'error')
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    {
      id: 'actions',
      label: 'Ações',
      width: 110,
      element: row => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={podeGerenciar ? 'Editar' : 'Sem permissão para editar etiquetas'}>
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!podeGerenciar}
                onClick={() => openEdit(row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={podeGerenciar ? 'Excluir' : 'Sem permissão para excluir etiquetas'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!podeGerenciar}
                onClick={() => setItemToDelete(row)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
    { id: 'name', label: 'Nome', element: row => row.name || '—' },
    {
      id: 'zpl',
      label: 'ZPL',
      element: row => (
        <Tooltip title={row.zpl || ''}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              maxWidth: 380,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.zpl || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'active',
      label: 'Situação',
      width: 110,
      element: row => (
        <Chip
          size="small"
          label={row.active ? 'Ativo' : 'Inativo'}
          color={row.active ? 'success' : 'default'}
          variant={row.active ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'createdAt',
      label: 'Criação',
      width: 170,
      element: row => formatDateTime(row.createdAt) || '—',
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Etiquetas ZPL"
        subtitle="Layouts de etiqueta enviados para a impressora Zebra."
        actions={
          <>
            <ExportMenu
              rows={rows}
              resolveRows={fetchAllForExport}
              columns={EXPORT_COLUMNS}
              fileName="etiquetas-zpl"
              title="Etiquetas ZPL"
              disabled={total === 0}
            />
            <PermissionComponent permissao={PERMISSOES.RFID_GERENCIAR}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!subCompanyId}
                onClick={openCreate}
              >
                Criar
              </Button>
            </PermissionComponent>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => {
                setSubCompanyId(event.target.value)
                setPage(0)
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Procurar por nome"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {!subCompanyId ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Selecione uma filial para listar as etiquetas ZPL.
          </Typography>
        </Paper>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={event => {
            setRowsPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowKey={row => row.id}
          emptyMessage="Nenhuma etiqueta ZPL encontrada."
        />
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar etiqueta ZPL' : 'Criar etiqueta ZPL'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ pt: 1 }} alignItems="flex-start">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={Boolean(editingId)}>
                <InputLabel id="printer-zpl-subcompany-label">Filial para cadastro</InputLabel>
                <Select
                  labelId="printer-zpl-subcompany-label"
                  label="Filial para cadastro"
                  value={form.subCompanyId}
                  onChange={updateForm('subCompanyId')}
                  MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                >
                  {(subCompany || []).map(item => (
                    <MenuItem key={item.subCompanyId} value={item.subCompanyId}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Nome"
                value={form.name}
                onChange={updateForm('name')}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                required
                multiline
                minRows={16}
                maxRows={16}
                label="ZPL"
                value={form.zpl}
                onChange={updateForm('zpl')}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13, alignItems: 'flex-start' } }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Pré-visualização
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'auto',
                  bgcolor: 'grey.50',
                }}
              >
                {previewLoading ? (
                  <CircularProgress size={28} />
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Pré-visualização da etiqueta"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                    Clique em &quot;Pré-visualizar&quot; para ver a etiqueta.
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={form.active}
                    onChange={event =>
                      setForm(current => ({ ...current, active: event.target.checked }))
                    }
                  />
                }
                label={form.active ? 'Ativo' : 'Inativo'}
              />
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handlePreview}
                disabled={previewLoading || !form.zpl.trim()}
              >
                Pré-visualizar
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} color="inherit">
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !isFormValid}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(itemToDelete)}
        title="Excluir etiqueta ZPL"
        description="Tem certeza que deseja excluir esse item?"
        confirmLabel="Excluir"
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setItemToDelete(null)}
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default PrinterZpl
